import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { Users, Bot, Plus, UserPlus, Copy } from 'lucide-react';

export default function PartnerPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('Users');
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '', full_name: '', password: '', company_name: '', phone: '', plan: 'demo',
  });
  const [creating, setCreating] = useState(false);
  const [lastInviteToken, setLastInviteToken] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [u, a] = await Promise.all([
        api.get('/partner/users'),
        api.get('/partner/agents'),
      ]);
      setUsers(u.data.users || []);
      setAgents(a.data.agents || []);
    } catch {
      toast.error('Failed to load partner data');
    } finally { setLoading(false); }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.password || !createForm.full_name) {
      return toast.error('Email, password and name required');
    }
    setCreating(true);
    try {
      const r = await api.post('/partner/users', createForm);
      toast.success('User created');
      setLastInviteToken(r.data.invite_token || '');
      setCreateForm({ email: '', full_name: '', password: '', company_name: '', phone: '', plan: 'demo' });
      setShowCreate(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Create failed');
    } finally { setCreating(false); }
  };

  const removeUser = async (id, email) => {
    if (!window.confirm(`Remove ${email}? Account will be scheduled for deletion in 7 days.`)) return;
    try {
      await api.delete(`/partner/users/${id}`);
      toast.success('User removed');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Remove failed');
    }
  };

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  return (
    <div className="page">
      <h1 className="page-title">Partner Cabinet</h1>

      {lastInviteToken && (
        <div className="banner banner--info">
          <span>
            Invite token for new user (share with them — they'll verify email):
            <code style={{ marginLeft: 8 }}>{lastInviteToken}</code>
            <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => {
              navigator.clipboard.writeText(lastInviteToken);
              toast.success('Copied');
            }}><Copy size={12} /> Copy</button>
          </span>
        </div>
      )}

      <div className="tabs">
        {['Users', 'Agents'].map(t => (
          <button key={t} className={`tab ${tab === t ? 'tab--active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'Users' && (
        <div className="tab-content">
          <div className="section-header">
            <h2><Users size={18} /> {user?.role === 'admin' ? 'All Owners' : 'My Users'} ({users.length})</h2>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              <UserPlus size={16} /> Add User
            </button>
          </div>

          {showCreate && (
            <form className="form-section" onSubmit={submitCreate}>
              <h3>Add New User</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input className="input" type="email" required value={createForm.email}
                    onChange={e => setCreateForm({ ...createForm, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input className="input" required value={createForm.full_name}
                    onChange={e => setCreateForm({ ...createForm, full_name: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Password *</label>
                  <input className="input" type="password" minLength={8} required value={createForm.password}
                    onChange={e => setCreateForm({ ...createForm, password: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Plan</label>
                  <select className="input" value={createForm.plan}
                    onChange={e => setCreateForm({ ...createForm, plan: e.target.value })}>
                    <option value="demo">Demo</option>
                    <option value="kholop">Kholop</option>
                    <option value="boyar">Boyar</option>
                    <option value="knyaz">Knyaz</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Company</label>
                  <input className="input" value={createForm.company_name}
                    onChange={e => setCreateForm({ ...createForm, company_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input className="input" value={createForm.phone}
                    onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          )}

          {users.length === 0 ? (
            <div className="empty-state">
              <Users size={48} className="empty-icon" />
              <h3>No users yet</h3>
              <p>Add your first user to start managing their voice agents.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Email</th><th>Name</th><th>Plan</th><th>Balance</th><th>Status</th>
                  {user?.role === 'admin' && <th>Partner</th>}
                  <th>Actions</th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{u.full_name}</td>
                    <td><span className="badge">{u.plan}</span></td>
                    <td>${(u.balance || 0).toFixed(2)}</td>
                    <td><span className={`status-badge status-badge--${u.status}`}>{u.status}</span></td>
                    {user?.role === 'admin' && <td>{u.partner_email || '—'}</td>}
                    <td>
                      <button className="btn btn-sm btn-danger-ghost" onClick={() => removeUser(u.id, u.email)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'Agents' && (
        <div className="tab-content">
          <div className="section-header">
            <h2><Bot size={18} /> Agents ({agents.length})</h2>
            <Link to="/agents/new" className="btn btn-primary"><Plus size={16} /> Create Agent</Link>
          </div>

          {agents.length === 0 ? (
            <div className="empty-state">
              <Bot size={48} className="empty-icon" />
              <h3>No agents yet</h3>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Name</th><th>Owner</th><th>Type</th><th>Calls</th><th>Status</th><th>Synced</th></tr>
              </thead>
              <tbody>
                {agents.map(a => (
                  <tr key={a.id}>
                    <td><Link to={`/agents/${a.id}`}>{a.name}</Link></td>
                    <td>{a.owner_email}</td>
                    <td><span className={`badge badge--${a.type}`}>{a.type}</span></td>
                    <td>{a.call_count}</td>
                    <td><span className={`status-badge status-badge--${a.status}`}>{a.status}</span></td>
                    <td>{a.telnyx_synced ? '✓' : <span className="badge badge--warning">Not synced</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
